import {
  BenchmarkLineStyle,
  ForecastHorizonDayOptions,
  ForecastMethod,
  LineChartLineTypes,
  PlotType,
  SparkLineTypes,
  WidgetSparklineOptions,
  YAxisPosition,
  ConditionalType,
  WidgetPlotTypeOptions,
  FilterDateRangeValues,
  HandTypes,
} from '@/modules/ta/widget/widget.constants';
import {
  BubbleShapes,
  BulletShapes,
  GaugeBigNumberPosition,
  PictorialTypes,
  PieChartConstant,
  Position,
} from '@/modules/core/charts/am5/charts.constants';
import { RelativeDateRangeKey } from '@/modules/core/daterange/daterange.constants';

function buildPlotTypeTooltip(name, description) {
  // eslint-disable-next-line tap/no-raw-text-js
  return `<b>${name}</b><br/>${description}`;
}

export const DatagridDisplayLengthOptions = [
  { key: 3, value: '3' },
  { key: 5, value: '5' },
  { key: 10, value: '10' },
  { key: 15, value: '15' },
  { key: 20, value: '20' },
  { key: 25, value: '25' },
  { key: 50, value: '50' },
  { key: 75, value: '75' },
  { key: 100, value: '100' },
];

export const DatagridRowsPerPageOptions = [
  { key: 3, value: '3' },
  { key: 5, value: '5' },
  { key: 10, value: '10' },
  { key: 12, value: '12' },
  { key: 15, value: '15' },
  { key: 18, value: '18' },
  { key: 20, value: '20' },
  { key: 25, value: '25' },
];

export const DatagridDefaultRowsPerPage = 10;

export const DatagridNumberOfPageOptions = [
  { key: 1, value: '1' },
  { key: 3, value: '3' },
  { key: 5, value: '5' },
  { key: 10, value: '10' },
  { key: 15, value: '15' },
  { key: 20, value: '20' },
];

export const DatagridDefaultNumberOfPage = 3;

export const DatagridReportDisplayLengthKeys = {
  DEFAULT_OUI: null,
  DEFAULT: 'default',
  ALL: -1,
  EVERYTHING: 10000,
};

export function GetDatagridReportDisplayLengthOptions() {
  return [
    { key: DatagridReportDisplayLengthKeys.DEFAULT, value: i18n.$t('Default') },
    { key: 3, value: '3' },
    { key: 5, value: '5' },
    { key: 10, value: '10' },
    { key: 15, value: '15' },
    { key: 20, value: '20' },
    { key: 25, value: '25' },
    { key: 50, value: '50' },
    { key: 75, value: '75' },
    { key: 100, value: '100' },
    { key: 500, value: '500' },
    { key: 1000, value: '1000' },
    { key: 2000, value: '2000' },
    { key: 5000, value: '5000' },
    { key: DatagridReportDisplayLengthKeys.ALL, value: i18n.$t('All') },
  ];
}

export function GetBigNumberPlotTypeOptions() {
  return [
    {
      key: WidgetPlotTypeOptions.DEFAULT,
      label: i18n.$t('Default'),
      img: 'bignumber-default',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Default'),
        i18n.$t(
          'Highlight individual key metrics for quick insights at a glance with additional customizations available.'
        )
      ),
    },
    {
      key: WidgetPlotTypeOptions.MULTI_METRIC,
      label: i18n.$t('Multi Metric'),
      img: 'bignumber-multimetric',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Multi Metric'),
        i18n.$t(
          'Display a key metric prominently while providing supporting metrics for additional context and insights.'
        )
      ),
    },
    {
      key: WidgetPlotTypeOptions.COMPARISON,
      label: i18n.$t('Comparison'),
      img: 'bignumber-comparison',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Comparison'),
        i18n.$t(
          'Display a key metric alongside its historical data and numeric and percentage change for quick performance evaluation.'
        )
      ),
    },
    {
      key: WidgetPlotTypeOptions.SPARKLINE,
      label: i18n.$t('Sparkline'),
      img: 'bignumber-sparkline',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Sparkline'),
        i18n.$t(
          'Display a key metric with a mini trendline for quick insights into recent performance patterns.'
        )
      ),
    },
    {
      key: WidgetPlotTypeOptions.CLASSIC,
      label: i18n.$t('Classic'),
      img: 'bignumber-classic',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Classic'),
        i18n.$t('Highlight individual key metrics for quick insights at a glance.')
      ),
    },
  ];
}

export function GetDataGridPlotTypeOptions() {
  return [
    {
      key: PlotType.DEFAULT,
      label: i18n.$t('Default'),
      img: 'datagrid-default',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Default'),
        i18n.$t(
          'Display and compare detailed data across multiple metrics and categories in a structured table format with additional customizations available.'
        )
      ),
    },
    {
      key: PlotType.CLASSIC,
      label: i18n.$t('Classic'),
      img: 'datagrid-classic',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Classic'),
        i18n.$t(
          'Display and compare detailed data across multiple metrics and categories in a structured table format.'
        )
      ),
    },
    {
      key: PlotType.CONDITIONAL_MAP,
      label: i18n.$t('Conditional Map'),
      img: 'datagrid-conditionalmapping',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Conditional Map'),
        i18n.$t(
          'Visually indicate performance thresholds by applying color-coded rules to highlight key data points.'
        )
      ),
    },
    {
      key: PlotType.GROUPED_COLUMN,
      label: i18n.$t('Grouped Columns'),
      img: 'datagrid-groupedcolumn',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Grouped Column'),
        i18n.$t(
          'Organize and compare related metrics by grouping them for clearer data segmentation and analysis.'
        )
      ),
    },
    {
      key: PlotType.EMBEDDED_SPARKLINE,
      label: i18n.$t('Embedded Sparkline'),
      img: 'datagrid-embeddedsparklines',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Embedded Sparkline'),
        i18n.$t(
          'Display metrics alongside mini trendlines, providing granular insights into performance trends.'
        )
      ),
    },
    {
      key: PlotType.HEAT_MAP,
      label: i18n.$t('Heat Map'),
      img: 'datagrid-heatmap',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Heat Map'),
        i18n.$t(
          'Highlight data patterns or outliers by visually emphasizing higher or lower values within a dataset.'
        )
      ),
    },
  ];
}

export function GetLinePlotTypeOptions() {
  return [
    {
      key: PlotType.LINE_V2,
      label: i18n.$t('Line'),
      img: 'linechart-line',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Line'),
        i18n.$t('Visualize trends in data across sequential categories.')
      ),
    },
    {
      key: PlotType.AREA_V2,
      label: i18n.$t('Area'),
      img: 'linechart-area',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Area'),
        i18n.$t(
          'Visualize trends in multiple metrics across sequential categories while emphasizing the magnitude of their contributions.'
        )
      ),
    },
    {
      key: PlotType.STACKED_V2,
      label: i18n.$t('Stacked'),
      img: 'linechart-stacked',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Stacked'),
        i18n.$t(
          'Show cumulative values of multiple metrics across sequential categories emphasizing both individual contributions and overall trends.'
        )
      ),
    },
    {
      key: PlotType.FULL_STACKED_V2,
      label: i18n.$t('100% Stacked'),
      img: 'linechart-fullstacked',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('100% Stacked'),
        i18n.$t(
          'Show proportional contribution of different metrics to a total and their trend across sequential categories.'
        )
      ),
    },
  ];
}

export function GetBarPlotTypeOptions() {
  return [
    {
      key: PlotType.CLUSTERED_V2,
      label: i18n.$t('Clustered'),
      img: 'barchart-clustered',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Clustered'),
        i18n.$t(
          'Compare multiple metrics in a shared category in addition to comparing metrics across multiple categories.'
        )
      ),
    },
    {
      key: PlotType.STACKED_V2,
      label: i18n.$t('Stacked'),
      img: 'barchart-stacked',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Stacked'),
        i18n.$t(
          'Display cumulative contribution of different metrics to a total across categories.'
        )
      ),
    },
    {
      key: PlotType.FULL_STACKED_V2,
      label: i18n.$t('100% Stacked'),
      img: 'barchart-fullstacked',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('100% Stacked'),
        i18n.$t('Compare proportional contribution of different metrics across categories.')
      ),
    },
    {
      key: PlotType.LAYERED_V2,
      label: i18n.$t('Layered'),
      img: 'barchart-layered',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Layered'),
        i18n.$t(
          'Compare individual metrics side by side across categories while maintaining a clear distinction between them.'
        )
      ),
    },
    {
      key: PlotType.LOLLIPOP_V2,
      label: i18n.$t('Lollipop'),
      img: 'barchart-lollipop',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Lollipop'),
        i18n.$t(
          'Emphasize individual metric data points in a minimalist design for clear comparison across categories.'
        )
      ),
    },
    {
      key: PlotType.RADIAL_HISTOGRAM_V2,
      label: i18n.$t('Radial Histogram'),
      img: 'barchart-histogram',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Radial Histogram'),
        i18n.$t('Visualize data distribution across categories in a circular format.')
      ),
    },
    {
      key: PlotType.RADIAL_BAR_V2,
      label: i18n.$t('Radial Bar'),
      img: 'barchart-radialbar',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Radial Bar'),
        i18n.$t(
          'Display cumulative contribution of different metrics to a total across categories in a circular bar chart.'
        )
      ),
    },
  ];
}

export function GetComboPlotTypeOptions() {
  return [
    {
      key: PlotType.CLUSTERED_V2,
      label: i18n.$t('Clustered'),
      img: 'combochart-clustered',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Clustered'),
        i18n.$t(
          'Compare related metrics in a shared category in addition to comparing them across multiple categories and highlight relationships between them.'
        )
      ),
    },
    {
      key: PlotType.STACKED_V2,
      label: i18n.$t('Stacked'),
      img: 'combochart-stacked',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Stacked'),
        i18n.$t(
          'Display cumulative contribution of related metrics to a total across categories and highlight relationships between them.'
        )
      ),
    },
    {
      key: PlotType.FULL_STACKED_V2,
      label: i18n.$t('100% Stacked'),
      img: 'combochart-fullstacked',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('100% Stacked'),
        i18n.$t(
          'Compare proportional contribution of related metrics across categories and highlight relationships between them.'
        )
      ),
    },
    {
      key: PlotType.LAYERED_V2,
      label: i18n.$t('Layered'),
      img: 'combochart-layered',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Layered'),
        i18n.$t(
          'Compare related metrics side by side across categories while maintaining a clear distinction between them and highlight relationships between them.'
        )
      ),
    },
  ];
}

export function GetGeoPlotTypeOptions() {
  return [
    {
      key: PlotType.BUBBLE_MAP_V2,
      label: i18n.$t('Bubble Map'),
      img: 'geochart-bubble',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Bubble Map'),
        i18n.$t('Visualize metric values as bubbles displayed across geographic regions.')
      ),
    },
    {
      key: PlotType.HEAT_MAP_V2,
      label: i18n.$t('Heat Map'),
      img: 'geochart-heatmap',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Heat Map'),
        i18n.$t('View intensity of data across locations using gradient color patterns.')
      ),
    },
  ];
}

export function GetGeoMapBoxPlotTypeOption() {
  return {
    key: PlotType.MAPBOX,
    label: i18n.$t('TapMap'),
    img: 'geochart-tapmap',
    tooltip: buildPlotTypeTooltip(
      i18n.$t('TapMap'),
      i18n.$t(
        'Display geo-boundaries with color gradients and bubbles according to metric value and data point count across layers.'
      )
    ),
  };
}

export function GetFunnelPlotTypeOptions() {
  return [
    {
      key: PlotType.FUNNEL,
      label: i18n.$t('Funnel'),
      img: 'funnelchart-default',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Funnel'),
        i18n.$t(
          'Visualize sequential stages of a process and identify drop-off rates between stages.'
        )
      ),
    },
    {
      key: PlotType.PYRAMID,
      label: i18n.$t('Pyramid'),
      img: 'funnelchart-pyramid',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Pyramid'),
        i18n.$t('Represent hierarchical data emphasizing relative size of categories.')
      ),
    },
    {
      key: PlotType.PICTORIAL,
      label: i18n.$t('Pictorial'),
      img: 'funnelchart-pictorial',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Pictorial'),
        i18n.$t(
          'Represent data visually with icons or shapes to make it more engaging and easier to understand proportions.'
        )
      ),
    },
  ];
}

export function GetPiePlotTypeOptions() {
  return [
    {
      key: PieChartConstant.PLOT_V2_PIE,
      label: i18n.$t('Pie Chart'),
      img: 'piechart-default',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Pie Chart'),
        i18n.$t('Show proportional distribution or percentage share of categories within a whole.')
      ),
    },
    {
      key: PieChartConstant.PLOT_DONUT,
      label: i18n.$t('Donut'),
      img: 'piechart-donut',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Donut'),
        i18n.$t(
          'Show proportional distribution of categories within a whole with space in the center for additional contextual information.'
        )
      ),
    },
  ];
}

export function GetGaugeChartPlotTypeOptions() {
  return [
    {
      key: PlotType.GAUGE_V2,
      label: i18n.$t('Gauge'),
      img: 'gaugechart-default',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Gauge'),
        i18n.$t('Visualize progress of a performance metric towards a target value at a glance.')
      ),
    },
    {
      key: PlotType.MULTI_AXIS_GAUGE,
      label: i18n.$t('Multi-axis Gauge'),
      img: 'gaugechart-multiaxis',
      tooltip: buildPlotTypeTooltip(
        i18n.$t('Multi-axis Gauge'),
        i18n.$t(
          'Compare and track multiple performance metrics against their respective targets on a single chart.'
        )
      ),
    },
  ];
}

export const DateFormatType = {
  DEFAULT: 'default',
  NO_YEAR: 'noYear',
};

export const GradientTypes = {
  SOLID: 'solid',
  LINEAR: 'linear',
  LINEAR_Y: 'linear-y',
  RADIAL: 'radial',
};

export const PieChartDrawOption = {
  MAX_RADIUS: 70,
  MAX_FREE_DEPTH: 20,
  DEFAULT_START_ANGLE: -90, // This is amchart default
  OTHERS_METRIC_LABEL: 'Others',
  SHOW_LABEL_PERCENT: '{valuePercentTotal}%',
  SHOW_VALUE: '{value}',
  SHOW_CATEGORY: '{category}',
  SHOW_TOOLTIP: '{metric}: {rawValue} ({percent}%)',
  SHOW_VALUE_PIE: 'value',
  SHOW_METRIC_PIE: 'metric',
  SHOW_RAW_VALUE: '{rawValue}',
  SHOW_METRIC: '{metric}',
  NULL_VALUE: 'Unspecified',
};

export const MapTypes = {
  STREET: 'street',
  OUTDOOR: 'outdoor',
  LIGHT: 'light',
  DARK: 'dark',
  SATELLITE: 'satellite',
  SATELLITE_STREET: 'satelliteStreet',
  NAVIGATION_DAY: 'navigationDay',
  NAVIGATION_NIGHT: 'navigationNight',
};
export const TapMapConfig = {
  BOUNDARY: 'boundary',
  CLUSTER: 'cluster',
  LATLONG: 'latlong',
  ZIPCODE: 'zipcode',
  COUNTY: 'county',
  CITY: 'city',
};

export const GradientTargets = {
  EVERYTHING: 'everything',
  HAND: 'hand',
  METRIC: 'metric',
  BAND: 'band',
  HAND_METRIC: 'hand_metric',
  HAND_BAND: 'hand_band',
  METRIC_BAND: 'metric_band',
};

export const LiveFormulaValues = {
  NA: 'NA',
  SUM: 'SUM',
  AVG: 'AVG',
  AVG_NO_ZERO: 'AVG_NO_ZERO',
  MAX: 'MAX',
  MIN: 'MIN',
};

export const WebBorderStyle = {
  SOLID: 'solid',
  DASHED: 'dashed',
  DOUBLE: 'double',
  DOTTED: 'dotted',
  GROOVE: 'groove',
  RIDGE: 'ridge',
  INSET: 'inset',
  OUTSET: 'outset',
};

export const ClientFields = {
  CLIENT: 'Client',
  CLIENT_ID: 'Client ID',
  CLIENT_BILLING_ID: 'Client Billing ID',
  CLIENT_FIELD_ID: 'client_id',
  CUSTOMER_ID: 'customer_id',
};

export const TemplateSortIds = {
  NAME: 'name',
  TEXT: 'text',
  ID_NAME: 'id_name',
};

export function GetWidgetBorderStyleSelectOptions() {
  return [
    { key: WebBorderStyle.SOLID, value: i18n.$t('Solid') },
    { key: WebBorderStyle.DASHED, value: i18n.$t('Dashed') },
    { key: WebBorderStyle.DOUBLE, value: i18n.$t('Double') },
    { key: WebBorderStyle.DOTTED, value: i18n.$t('Dotted') },
    { key: WebBorderStyle.GROOVE, value: i18n.$t('Groove') },
    { key: WebBorderStyle.RIDGE, value: i18n.$t('Ridge') },
    { key: WebBorderStyle.INSET, value: i18n.$t('Inset') },
    { key: WebBorderStyle.OUTSET, value: i18n.$t('Outset') },
  ];
}

export function GetDateFormatSelectOptions() {
  return [
    { key: DateFormatType.DEFAULT, value: i18n.$t('Include year') },
    { key: DateFormatType.NO_YEAR, value: i18n.$t("Don't include year") },
  ];
}

export function GetLineChartLineTypesSelectOptions() {
  return [
    { key: LineChartLineTypes.LINE, value: i18n.$t('Line') },
    { key: LineChartLineTypes.STEP_LINE, value: i18n.$t('Step Line') },
  ];
}

export function GetBenchmarkLineSelectOptions() {
  return [
    { key: BenchmarkLineStyle.DASHED, value: i18n.$t('Dashed') },
    { key: BenchmarkLineStyle.DOTTED, value: i18n.$t('Dotted') },
    { key: BenchmarkLineStyle.SOLID, value: i18n.$t('Solid') },
    { key: BenchmarkLineStyle.NONE, value: i18n.$t('None') },
  ];
}

export function GetForecastLineStyleSelectOptions() {
  return [
    { key: BenchmarkLineStyle.DASHED, value: i18n.$t('Dashed') },
    { key: BenchmarkLineStyle.DOTTED, value: i18n.$t('Dotted') },
    { key: BenchmarkLineStyle.SOLID, value: i18n.$t('Solid') },
  ];
}

export function GetForecastMethodSelectOptions() {
  return [
    { key: ForecastMethod.AUTO, value: i18n.$t('Auto') },
    { key: ForecastMethod.LINEAR, value: i18n.$t('Linear') },
    { key: ForecastMethod.SEASONAL, value: i18n.$t('Seasonal') },
  ];
}

export function GetForecastHorizonSelectOptions() {
  return ForecastHorizonDayOptions.map((days) => ({
    key: days,
    value: i18n.$t('{days} days', { days }),
  }));
}

export function GetYAxisPositionSelectOptions() {
  return [
    { key: YAxisPosition.DEFAULT, value: i18n.$t('Default') },
    { key: YAxisPosition.SWAP, value: i18n.$t('Swap Y Axes') },
    { key: YAxisPosition.LEFT, value: i18n.$t('All Left') },
    { key: YAxisPosition.RIGHT, value: i18n.$t('All Right') },
  ];
}

export function GetBulletsShapeSelectOptions() {
  return [
    { key: BulletShapes.CIRCLE, value: i18n.$t('Circle') },
    { key: BulletShapes.DIAMOND, value: i18n.$t('Diamond') },
    { key: BulletShapes.STAR, value: i18n.$t('Star') },
    { key: BulletShapes.TRIANGLE, value: i18n.$t('Triangle') },
  ];
}

export function GetBubbleShapeSelectOptions() {
  return [
    { key: BubbleShapes.CIRCLE, value: i18n.$t('Circle') },
    { key: BubbleShapes.DIAMOND, value: i18n.$t('Diamond') },
    { key: BubbleShapes.STAR, value: i18n.$t('Star') },
    { key: BubbleShapes.TRIANGLE, value: i18n.$t('Triangle') },
  ];
}

export function GetGradientColorSelectOptions() {
  return [
    { key: GradientTypes.SOLID, value: i18n.$t('Solid') },
    { key: GradientTypes.LINEAR, value: i18n.$t('Gradient (Vertical)') },
    { key: GradientTypes.LINEAR_Y, value: i18n.$t('Gradient (Horizontal)') },
  ];
}

export function GetPictorialIconSelectOptions() {
  return [
    { key: PictorialTypes.MALE, value: i18n.$t('Male') },
    { key: PictorialTypes.FEMALE, value: i18n.$t('Female') },
    { key: PictorialTypes.MUG, value: i18n.$t('Mug') },
    { key: PictorialTypes.CUP, value: i18n.$t('Cup') },
    { key: PictorialTypes.KEY, value: i18n.$t('Key') },
    { key: PictorialTypes.APPLE, value: i18n.$t('Apple') },
    { key: PictorialTypes.MOUNTAIN, value: i18n.$t('Mountain') },
    { key: PictorialTypes.SAILBOAT, value: i18n.$t('Sailboat') },
    { key: PictorialTypes.BULB, value: i18n.$t('Bulb') },
    { key: PictorialTypes.THERMOMETER, value: i18n.$t('Thermometer') },
    { key: PictorialTypes.TREE, value: i18n.$t('Tree') },
    { key: PictorialTypes.ROCKETSHIP, value: i18n.$t('Rocket Ship') },
    { key: PictorialTypes.HAMMER, value: i18n.$t('Hammer') },
    { key: PictorialTypes.SKYSCRAPER, value: i18n.$t('Skyscraper') },
    { key: PictorialTypes.EVERGREEN, value: i18n.$t('Evergreen') },
    { key: PictorialTypes.HEART, value: i18n.$t('Heart') },
    { key: PictorialTypes.HOUSE, value: i18n.$t('House') },
  ];
}

export function GetPieGradientColorSelectOptions() {
  return [
    { key: GradientTypes.SOLID, value: i18n.$t('Solid') },
    { key: GradientTypes.LINEAR, value: i18n.$t('Linear') },
  ];
}

export function GetPieDonutGradientColorSelectOptions() {
  return [
    { key: GradientTypes.SOLID, value: i18n.$t('Solid') },
    { key: GradientTypes.LINEAR, value: i18n.$t('Linear') },
    { key: GradientTypes.RADIAL, value: i18n.$t('Radial') },
  ];
}

export function GetPieLegendPositionOptions() {
  return [
    { key: Position.BOTTOM, value: i18n.$t('Bottom') },
    { key: Position.LEFT, value: i18n.$t('Left') },
    { key: Position.RIGHT, value: i18n.$t('Right') },
  ];
}

export function GetBubbleGradientColorSelectOptions() {
  return [
    { key: GradientTypes.SOLID, value: i18n.$t('Solid') },
    { key: GradientTypes.LINEAR, value: i18n.$t('Linear') },
    { key: GradientTypes.RADIAL, value: i18n.$t('Radial') },
  ];
}

export function GetGaugeHandTypeOptions() {
  return [
    { key: HandTypes.NONE, value: i18n.$t('None') },
    { key: HandTypes.NEEDLE, value: i18n.$t('Needle') },
    { key: HandTypes.POINTER, value: i18n.$t('Pointer') },
    { key: HandTypes.TRIANGLE, value: i18n.$t('Triangle') },
    { key: HandTypes.LINE, value: i18n.$t('Line') },
  ];
}

export function GetGaugeBigNumberPositionSelectOptions() {
  return [
    { key: GaugeBigNumberPosition.NONE, value: i18n.$t('None') },
    { key: GaugeBigNumberPosition.HAND, value: i18n.$t('Inside Pointer (Colored)') },
    {
      key: GaugeBigNumberPosition.HAND_TRANSPARENT,
      value: i18n.$t('Inside Pointer (Transparent)'),
    },
    { key: GaugeBigNumberPosition.INSIDE, value: i18n.$t('Inside Gauge') },
    { key: GaugeBigNumberPosition.BELOW, value: i18n.$t('Below Gauge') },
  ];
}

export const SelectOptions = {
  FIELD: 'field',
  LABEL: 'label',
};

export function GetGeoMapTypeSelectOptions() {
  return [
    { key: MapTypes.STREET, value: i18n.$t('Street') },
    { key: MapTypes.OUTDOOR, value: i18n.$t('OutDoor') },
    { key: MapTypes.LIGHT, value: i18n.$t('Light') },
    { key: MapTypes.DARK, value: i18n.$t('Dark') },
    { key: MapTypes.SATELLITE, value: i18n.$t('Satellite') },
    { key: MapTypes.SATELLITE_STREET, value: i18n.$t('Satellite Street') },
    { key: MapTypes.NAVIGATION_DAY, value: i18n.$t('Navigation Day') },
    { key: MapTypes.NAVIGATION_NIGHT, value: i18n.$t('Navigation Night') },
  ];
}

export function GetTapMapConfigSelectOptions() {
  return [
    { key: TapMapConfig.BOUNDARY, value: i18n.$t('Boundary') },
    { key: TapMapConfig.CLUSTER, value: i18n.$t('Cluster') },
  ];
}

export function GetSparkLineTypeSelectOption() {
  return [
    { key: SparkLineTypes.COLUMN, value: i18n.$t('Bar') },
    { key: SparkLineTypes.LINE, value: i18n.$t('Area') },
  ];
}

export function GetSparkLineDataSelectOption() {
  return [
    { key: WidgetSparklineOptions.LAST_6_MONTHS, value: i18n.$t('Last 6 months') },
    { key: WidgetSparklineOptions.LAST_3_MONTHS, value: i18n.$t('Last 3 months') },
  ];
}

export const DataFetchSystem = {
  STORED: 'stored',
  LIVE: 'live',
};

export function GetDataFetchSystemValues() {
  return [
    { key: DataFetchSystem.STORED, value: i18n.$t('Stored Data') },
    { key: DataFetchSystem.LIVE, value: i18n.$t('On Demand Data') },
  ];
}

export function GetConditionSelectOptions() {
  return [
    { key: ConditionalType.GREATER_THAN, value: i18n.$t('Greater Than') },
    { key: ConditionalType.LESS_THAN, value: i18n.$t('Less Than') },
    { key: ConditionalType.BETWEEN, value: i18n.$t('Between') },
  ];
}

export function GetRelativeDateRangeSelectOptions() {
  return [
    { key: RelativeDateRangeKey.DEFAULT, value: i18n.$t('Default (Dashboard Date)') },
    { key: RelativeDateRangeKey.TODAY, value: i18n.$t('Today') },
    { key: RelativeDateRangeKey.YESTERDAY, value: i18n.$t('Yesterday') },
    { key: RelativeDateRangeKey.TOMORROW, value: i18n.$t('Tomorrow') },
    { key: RelativeDateRangeKey.LAST_7_DAYS, value: i18n.$t('Last 7 Days') },
    { key: RelativeDateRangeKey.LAST_14_DAYS, value: i18n.$t('Last 14 Days') },
    { key: RelativeDateRangeKey.LAST_30_DAYS, value: i18n.$t('Last 30 Days') },
    { key: RelativeDateRangeKey.LAST_90_DAYS, value: i18n.$t('Last 90 Days') },
    { key: RelativeDateRangeKey.LAST_180_DAYS, value: i18n.$t('Last 180 Days') },
    { key: RelativeDateRangeKey.LAST_WEEK, value: i18n.$t('Last Week') },
    { key: RelativeDateRangeKey.LAST_MONTH, value: i18n.$t('Last Month') },
    { key: RelativeDateRangeKey.LAST_3_MONTHS, value: i18n.$t('Last 3 Months') },
    { key: RelativeDateRangeKey.LAST_QUARTER, value: i18n.$t('Last Quarter') },
    { key: RelativeDateRangeKey.LAST_6_MONTHS, value: i18n.$t('Last 6 Months') },
    { key: RelativeDateRangeKey.LAST_12_MONTHS, value: i18n.$t('Last 12 Months') },
    { key: RelativeDateRangeKey.LAST_YEAR, value: i18n.$t('Last Year') },
    { key: RelativeDateRangeKey.LAST_13_MONTHS, value: i18n.$t('Last 13 Months') },
    { key: RelativeDateRangeKey.YEAR_TO_DATE, value: i18n.$t('Year to Date') },
    { key: RelativeDateRangeKey.THIS_WEEK, value: i18n.$t('This Week') },
    { key: RelativeDateRangeKey.THIS_MONTH, value: i18n.$t('This Month') },
    {
      key: RelativeDateRangeKey.THIS_MONTH_EXCLUDE_TODAY,
      value: i18n.$t('This Month (Exclude Today)'),
    },
    { key: RelativeDateRangeKey.THIS_QUARTER, value: i18n.$t('This Quarter') },
    { key: RelativeDateRangeKey.THIS_YEAR, value: i18n.$t('This Year') },
    { key: RelativeDateRangeKey.NEXT_7_DAYS, value: i18n.$t('Next 7 Days') },
    { key: RelativeDateRangeKey.NEXT_30_DAYS, value: i18n.$t('Next 30 Days') },
    { key: RelativeDateRangeKey.NEXT_WEEK, value: i18n.$t('Next Week') },
    { key: RelativeDateRangeKey.NEXT_MONTH, value: i18n.$t('Next Month') },
    { key: RelativeDateRangeKey.NEXT_YEAR, value: i18n.$t('Next Year') },
  ];
}

export function GetFilterDateRangeSelectOptions() {
  return [
    { key: FilterDateRangeValues.CUSTOM, value: i18n.$t('Custom') },
    { key: FilterDateRangeValues.RELATIVE, value: i18n.$t('Relative') },
  ];
}

export function GetLiveFormulaItemSelectOptions() {
  return [
    { key: LiveFormulaValues.NA, value: i18n.$t('Do not aggregate') },
    { key: LiveFormulaValues.SUM, value: i18n.$t('Sum') },
    { key: LiveFormulaValues.AVG, value: i18n.$t('Average') },
    { key: LiveFormulaValues.AVG_NO_ZERO, value: i18n.$t('Average, No Zeros') },
    { key: LiveFormulaValues.MAX, value: i18n.$t('Maximum') },
    { key: LiveFormulaValues.MIN, value: i18n.$t('Minimum') },
  ];
}

export const GeoLimitSelectOptions = [
  { key: 1000, value: '1000' },
  { key: 2000, value: '2000' },
  { key: 5000, value: '5000' },
];

export function GetGaugeGradientTargetSelectOptions() {
  return [
    { key: GradientTargets.EVERYTHING, value: i18n.$t('Everything') },
    { key: GradientTargets.HAND, value: i18n.$t('Hand') },
    { key: GradientTargets.METRIC, value: i18n.$t('Metric') },
    { key: GradientTargets.BAND, value: i18n.$t('Band') },
    { key: GradientTargets.HAND_METRIC, value: i18n.$t('Hand and Metric') },
    { key: GradientTargets.HAND_BAND, value: i18n.$t('Hand and Band') },
    { key: GradientTargets.METRIC_BAND, value: i18n.$t('Metric and Band') },
  ];
}
